<template>
  <div class="public-products-page">
    <!-- 头部导航 -->
    <header class="header">
      <div class="header-left">
        <div class="user-info">
          <div class="user-name">{{ $t('publicHome.welcomeGuest') }}</div>
          <div class="user-stall">{{ $t('publicHome.guestSubtitle') }}</div>
        </div>
      </div>
      <div class="header-right">
        <lang-switcher />
        <button class="login-btn" @click="goToLogin">{{ $t('common.login') }} / {{ $t('common.register') }}</button>
      </div>
    </header>

    <!-- 搜索栏 -->
    <div class="search-bar-wrapper">
      <div class="search-bar-inner">
        <input
          type="text"
          v-model="searchKeyword"
          @input="onSearchInput"
          @keydown.enter="doSearch"
          @focus="showSuggestions = true"
          :placeholder="$t('publicHome.searchPlaceholder') || '搜索商品名称...'"
          class="search-input"
        />
        <button class="search-btn" @click="doSearch">🔍</button>
        <!-- 下拉提示 -->
        <div v-if="showSuggestions && suggestions.length > 0" class="suggestions-dropdown">
          <div
            v-for="(item, idx) in suggestions"
            :key="idx"
            class="suggestion-item"
            @mousedown.prevent="selectSuggestion(item)"
          >
            <span class="suggestion-name">{{ item.name }}</span>
            <span class="suggestion-sku">{{ item.sku || '' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 主体布局：左侧分类 + 右侧商品 -->
    <div class="main-layout">
      <!-- 左侧分类栏 -->
      <div class="category-sidebar">
        <div class="category-title">{{ $t('publicHome.category') }}</div>
        <div 
          :class="['category-item', { active: selectedCategory === '' }]"
          @click="filterCategory('')"
        >
          {{ $t('publicHome.all') }}
        </div>
        <div 
          v-for="cat in categories" 
          :key="cat.value"
          :class="['category-item', { active: selectedCategory === cat.value }]"
          @click="filterCategory(cat.value)"
        >
          <span class="category-icon">{{ cat.emoji }}</span>
          <span class="category-name">{{ cat.name }}</span>
        </div>
      </div>

      <!-- 右侧商品区域 -->
      <div class="product-list-wrapper">
        <!-- 商品列表 -->
        <div class="product-list">
          <!-- 首次加载状态（仅在没有商品时显示） -->
          <div v-if="loading && products.length === 0" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t('common.loading') }}</p>
          </div>

          <!-- 空状态 -->
          <div v-else-if="!loading && products.length === 0" class="empty-state">
            <div class="empty-icon">📦</div>
            <p>{{ $t('common.noData') }}</p>
          </div>

          <!-- 商品卡片（始终显示已加载的商品） -->
          <div 
            v-for="product in filteredProducts" 
            :key="product.id"
            class="product-item"
            @click="tryOrder(product)"
          >
            <!-- 图片 -->
            <div class="product-image" @click.stop="openImagePreview(product)">
              <img 
                v-if="product.mainImage" 
                :src="getImageUrl(product.mainImage)" 
                :alt="$t('publicHome.productImage')"
                class="product-img"
                @error="handleImageError(product)"
              />
              <div v-else class="product-icon">📦</div>
              <!-- 多图指示 -->
              <div v-if="getProductImages(product).length > 1" class="image-count-badge">
                📷 {{ getProductImages(product).length }}
              </div>
            </div>
            
            <!-- 右侧信息 -->
            <div class="product-info">
              <div class="product-header">
                <div class="product-name">{{ productName(product) }}</div>
                <div class="product-actions-right">
                  <button class="order-btn" @click.stop="tryOrder(product)">
                    {{ $t('publicHome.orderNow') }}
                  </button>
                </div>
              </div>
              
              <div class="product-meta">{{ product.categoryName || getCategoryName(product.categoryId) }} · SKU: {{ product.sku || 'N/A' }}</div>
              
              <div class="price-column">
                <div class="price-item">
                  <span class="price-label">{{ $t('publicHome.marketPrice') }}</span>
                  <span class="price-value">S$ {{ (product.marketPrice || 0).toFixed(2) }}</span>
                </div>
              </div>
              
              <div class="stock-column">
                <div class="stock-item">
                  <span class="stock-label">{{ $t('publicHome.minOrder') }}:</span>
                  <span class="stock-value">{{ product.minOrderQuantity || 1 }} {{ product.unit || 'kg' }}</span>
                </div>
                <div class="stock-item">
                  <span class="stock-label">{{ $t('publicHome.stock') }}:</span>
                  <span class="stock-value">{{ product.totalStock || 0 }} {{ product.unit || 'kg' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部加载状态 -->
        <div v-if="loading && products.length > 0" class="loading-more">
          <div class="loading-spinner"></div>
          <p>{{ $t('common.loading') }}</p>
        </div>
        <div v-else-if="noMore && products.length > 0" class="no-more">
          <p>{{ $t('PublicProducts.line146_text') }}</p>
        </div>
      </div>
    </div>

    <!-- 注册引导弹窗 -->
    <div v-if="showRegisterModal" class="modal-overlay" @click.self="closeRegisterModal">
      <div class="modal register-modal">
        <div class="modal-title">{{ $t('publicHome.registerTitle') }}</div>
        <div class="modal-subtitle">{{ $t('publicHome.registerSubtitle') }}</div>
        
        <div class="modal-benefits">
          <div class="benefit-item">
            <div class="benefit-icon">✓</div>
            <div class="benefit-text">{{ $t('publicHome.benefit1') }}</div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">✓</div>
            <div class="benefit-text">{{ $t('publicHome.benefit2') }}</div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">✓</div>
            <div class="benefit-text">{{ $t('publicHome.benefit3') }}</div>
          </div>
        </div>
        
        <div class="modal-actions">
          <button class="modal-btn modal-btn-secondary" @click="closeRegisterModal">
            {{ $t('publicHome.later') }}
          </button>
          <button class="modal-btn modal-btn-primary" @click="goToRegister">
            {{ $t('publicHome.registerNow') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 图片预览 -->
    <ImagePreview ref="imagePreview" />

    <!-- APP 下载浮动按钮 -->
    <div class="app-download-container">
      <a v-if="!isIOS" href="/app-release.apk" class="app-download-float-btn android-btn" download>
        🤖 Android
      </a>
      <a href="https://apps.apple.com/cn/app/wholesale/id6809011555" target="_blank" class="app-download-float-btn ios-btn">
        🍎 iOS
      </a>
    </div>

    <!-- WhatsApp 联系我们 -->
    <a v-if="whatsappLink" :href="whatsappLink" target="_blank" class="whatsapp-float-btn">
      {{ $t('loginPage.whatsappContact') }}
    </a>
  </div>
</template>

<script>
import api from '@/services/api';
import LangSwitcher from '@/components/LangSwitcher.vue';
import ImagePreview from '@/components/ImagePreview.vue';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'PublicProducts',
  components: { LangSwitcher, ImagePreview },
  data() {
    return {
      loading: false,
      categories: [],
      selectedCategory: '',
      searchKeyword: '',
      suggestions: [],
      showSuggestions: false,
      searchDebounce: null,
      products: [],
      showRegisterModal: false,
      wholesalerId: null,
      wholesalerInfo: null,
      // 上拉加载相关
      page: 1,
      pageSize: 20,
      noMore: false,
    };
  },
  computed: {
    isIOS() {
      if (typeof window === 'undefined' || !window.Capacitor) return false;
      const platform = window.Capacitor?.getPlatform?.();
      return platform === 'ios';
    },
    whatsappLink() {
      if (this.wholesalerInfo && this.wholesalerInfo.whatsappNumber) {
        const phone = this.wholesalerInfo.whatsappNumber.replace(/[^\d+]/g, '');
        return `https://wa.me/${phone}`;
      }
      return 'https://wa.me/6586031798';
    },
    filteredProducts() {
      if (!this.selectedCategory || this.selectedCategory === '') {
        return this.products;
      }
      const cat = this.categories.find(c => c.value === this.selectedCategory);
      if (!cat) return this.products;
      // 根据分类关键词过滤商品
      const keywords = cat.name.replace(/[类]/g, '');
      return this.products.filter(p => {
        const cn = (p.categoryName || '').toLowerCase();
        return cn.includes(keywords.toLowerCase());
      });
    }
  },
  async mounted() {
    // 先解析子域名，获取批发商ID
    await this.resolveWholesaler();
    // 加载商品和分类
    this.loadProducts();
    this.loadCategories();
    // 添加滚动监听
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    // 移除滚动监听
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    getImageUrl,
    /**
     * 从路由参数获取批发商ID
     * 支持 query 参数 (?wholesalerId=8) 和路由参数 (/wholesale/:wholesalerId)
     */
    getWholesalerId() {
      return this.$route.query.wholesalerId || this.$route.params.wholesalerId || null;
    },
    /**
     * 根据批发商ID解析批发商信息
     */
    async resolveWholesaler() {
      const wholesalerId = this.getWholesalerId();
      if (!wholesalerId) return;
      try {
        const res = await api.get(`/public/wholesale/${wholesalerId}`);
        if (res && res.id) {
          this.wholesalerId = res.id;
          this.wholesalerInfo = res;
          console.log('批发商信息加载成功:', wholesalerId, '→', res.companyName);
        }
      } catch(e) {
        console.warn('批发商信息加载失败:', wholesalerId, e);
      }
    },
    /**
     * 从已加载的商品中提取分类
     */
    extractCategories() {
      const categoryMap = {};
      this.products.forEach(p => {
        const name = p.categoryName || '其他';
        if (!categoryMap[name]) {
          categoryMap[name] = { name, count: 0 };
        }
        categoryMap[name].count++;
      });
      const predefined = {
        '蔬菜类': { value: 'vegetables', emoji: '🥬' },
        '水果类': { value: 'fruits', emoji: '🍎' },
        '肉类': { value: 'meat', emoji: '🥩' },
        '海鲜类': { value: 'seafood', emoji: '🦐' },
        '粮油类': { value: 'dry', emoji: '🍚' },
        '冷冻类': { value: 'frozen', emoji: '❄️' }
      };
      this.categories = Object.entries(categoryMap).map(([name, data]) => {
        const pre = predefined[name];
        return {
          value: pre ? pre.value : name,
          name,
          emoji: pre ? pre.emoji : '📦',
          count: data.count
        };
      });
    },
    async loadProducts(append = false) {
      if (this.loading || this.noMore) return;
      
      this.loading = true;
      try {
        const params = {
          page: this.page,
          size: this.pageSize
        };
        if (this.wholesalerId) {
          params.wholesalerId = this.wholesalerId;
        }
        const response = await api.get('/public/products', { params });
        const newProducts = response?.items || response?.products || [];
        const totalPages = response?.totalPages ?? 0;
        const currentPage = response?.page ?? this.page;
        
        if (append) {
          this.products = [...this.products, ...newProducts];
        } else {
          this.products = newProducts;
        }
        
        // 判断是否还有更多数据
        // 1. 当前页 >= 总页数
        // 2. 返回数据少于每页大小
        // 3. 没有数据
        if (currentPage >= totalPages || newProducts.length < this.pageSize || newProducts.length === 0) {
          this.noMore = true;
        }
        
        this.page++;
        
        // 加载分类
        this.loadCategories();
      } catch (error) {
        console.error('加载商品失败:', error);
        this.$toast?.error(this.$t('publicHome.loadFailed'));
      } finally {
        this.loading = false;
      }
    },
    async loadCategories() {
      try {
        const params = {};
        if (this.wholesalerId) {
          params.wholesalerId = this.wholesalerId;
        }
        const response = await api.get('/public/products/categories', { params });
        this.categories = response || [];
      } catch (error) {
        console.error('加载分类失败:', error);
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.loadProducts();
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    handleScroll() {
      // 前置检查：如果正在加载或没有更多数据，直接返回
      if (this.loading || this.noMore) return;
      
      // 判断是否滚动到页面底部
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const clientHeight = document.documentElement.clientHeight;
      const scrollHeight = document.documentElement.scrollHeight;
      
      // 距离底部还有 100px 时触发加载
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        this.loadProducts(true);
      }
    },
    async search() {
      const keyword = this.searchKeyword.trim();
      this.loading = true;
      try {
        const params = {};
        if (keyword) params.keyword = keyword;
        if (this.wholesalerId) params.wholesalerId = this.wholesalerId;
        const prodRes = await api.get('/public/products', { params });
        this.products = prodRes?.items || prodRes?.products || prodRes || [];
        // 从搜索结果中重新提取分类
        this.extractCategories();
        this.selectedCategory = '';
      } catch (error) {
        console.error('搜索失败:', error);
        this.$toast?.error(this.$t('publicHome.searchFailed'));
      } finally {
        this.loading = false;
      }
    },
    onSearchInput() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        const kw = this.searchKeyword.trim().toLowerCase();
        if (!kw) {
          this.suggestions = [];
          this.showSuggestions = false;
          return;
        }
        this.suggestions = this.products
          .filter(p => (p.name || '').toLowerCase().includes(kw))
          .slice(0, 8);
        this.showSuggestions = true;
      }, 200);
    },
    selectSuggestion(item) {
      this.searchKeyword = item.name;
      this.showSuggestions = false;
      this.suggestions = [];
      this.doSearch();
    },
    doSearch() {
      this.showSuggestions = false;
      this.search();
    },
    filterCategory(categoryId) {
      this.selectedCategory = categoryId;
    },
    handleImageError(product) {
      product.imageError = true;
    },
    /**
     * 获取商品的图片列表
     */
    getProductImages(product) {
      const images = [];
      if (product.mainImage) {
        images.push(this.getImageUrl(product.mainImage));
      }
      if (product.images) {
        let imgList = product.images;
        if (typeof imgList === 'string') {
          try { imgList = JSON.parse(imgList); } catch(e) { imgList = []; }
        }
        if (Array.isArray(imgList)) {
          imgList.forEach(img => {
            const url = typeof img === 'string' ? img : img.url;
            if (url && !images.includes(url)) {
              images.push(this.getImageUrl(url));
            }
          });
        }
      }
      return images;
    },
    /**
     * 打开图片预览
     */
    openImagePreview(product) {
      const images = this.getProductImages(product);
      if (images.length > 0) {
        this.$refs.imagePreview.open(images, 0);
      }
    },
    showLoginModal() {
      this.showRegisterModal = true;
    },
    tryOrder(product) {
      // 未登录状态，跳转到登录页面，携带批发商ID
      const query = {};
      if (this.wholesalerId) {
        query.wholesalerId = this.wholesalerId;
      }
      this.$router.push({ path: '/login', query });
    },
    closeRegisterModal() {
      this.showRegisterModal = false;
    },
    goToLogin() {
      const query = {};
      if (this.wholesalerId) {
        query.wholesalerId = this.wholesalerId;
      }
      this.$router.push({ path: '/login', query });
    },
    goToRegister() {
      const query = {};
      if (this.wholesalerId) {
        query.wholesalerId = this.wholesalerId;
      }
      this.$router.push({ path: '/register', query });
    },
    getCategoryName(categoryId) {
      const cat = this.categories.find(c => c.value === categoryId);
      return cat ? cat.name : '';
    },
    productName(product) {
      return product.name || product.productName || 'Unnamed Product';
    },
    handleIosDownload() {
      window.open('https://apps.apple.com/cn/app/wholesale/id6809011555', '_blank');
    }
  }
};
</script>

<style scoped>
.public-products-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
}

/* ==================== 头部导航 ==================== */
.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 20px;
  padding-top: calc(16px + env(safe-area-inset-top, 0px));
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-info {
  text-align: left;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
}

.user-stall {
  font-size: 12px;
  opacity: 0.9;
}

.header-right {
  display: flex;
  gap: 8px;
  align-items: center;
}

.lang-switch {
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
}

.login-btn {
  background: white;
  color: #667eea;
  border: none;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

/* ==================== 搜索栏 ==================== */
.search-bar-wrapper {
  padding: 12px 16px 0;
  position: sticky;
  top: 60px;
  z-index: 90;
  background: #f5f5f5;
}

.search-bar-inner {
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  overflow: visible;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 16px;
  font-size: 14px;
  background: transparent;
  border-radius: 24px 0 0 24px;
}

.search-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 10px 18px;
  border-radius: 0 24px 24px 0;
  cursor: pointer;
  font-size: 16px;
}

.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  margin-top: 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
}

.suggestion-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.15s;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover {
  background: #f0f4ff;
}

.suggestion-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.suggestion-sku {
  font-size: 12px;
  color: #999;
}

/* ==================== 主体布局 ==================== */
.main-layout {
  display: flex;
  gap: 16px;
  padding: 16px;
  min-height: calc(100vh - 200px);
}

/* 左侧分类栏 */
.category-sidebar {
  width: 70px;
  flex-shrink: 0;
  background: white;
  border-radius: 8px;
  padding: 12px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.category-title {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  text-align: center;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 6px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  background: #f5f5f5;
  min-height: 60px;
  justify-content: center;
}

.category-item:hover {
  background: #f0f4ff;
}

.category-item.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(102,126,234,0.3);
}

.category-icon {
  font-size: 20px;
}

.category-name {
  font-size: 11px;
  text-align: center;
}

/* 右侧商品列表 */
.product-list-wrapper {
  flex: 1;
  min-width: 0;
}

/* 商品列表 */
.product-list {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

/* 商品卡片 */
.product-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: white;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.product-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.product-item:active {
  transform: scale(0.99);
}

/* 商品图片 */
.product-image {
  flex: 0 0 80px;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f0f0f0, #e0e0e0);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: transform 0.2s;
}

.product-image:hover {
  transform: scale(1.05);
}

.product-image:active {
  transform: scale(0.98);
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}

.product-icon {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
}

/* 多图指示器 */
.image-count-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 9px;
  padding: 1px 5px;
  border-radius: 8px;
  backdrop-filter: blur(4px);
  pointer-events: none;
}

/* 商品信息 */
.product-info {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.product-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  text-align: left;
  flex: 1;
}

.product-actions-right {
  flex-shrink: 0;
}

.order-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
}

.order-btn:hover {
  opacity: 0.9;
}

.product-meta {
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
}

/* 价格 */
.price-column {
  margin-bottom: 8px;
}

.price-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.price-label {
  font-size: 12px;
  color: #999;
}

.price-value {
  font-size: 18px;
  font-weight: 700;
  color: #ff4d4f;
}

/* 库存 */
.stock-column {
  display: flex;
  gap: 12px;
}

.stock-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.stock-label {
  color: #999;
}

.stock-value {
  color: #333;
  font-weight: 500;
}

/* 加载和空状态 */
.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

/* 上拉加载状态 */
.loading-more,
.no-more {
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 14px;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ==================== 弹窗 ==================== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  padding: 24px;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-title {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 8px;
  color: #333;
}

.modal-subtitle {
  text-align: center;
  font-size: 14px;
  color: #999;
  margin-bottom: 24px;
}

.modal-benefits {
  margin-bottom: 24px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 14px;
}

.benefit-icon {
  width: 24px;
  height: 24px;
  background: #4CAF50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.benefit-text {
  color: #333;
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.modal-btn {
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  border: none;
  cursor: pointer;
}

.modal-btn-secondary {
  background: #f5f5f5;
  color: #333;
}

.modal-btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* ==================== 响应式 ==================== */
/* 平板和手机：保持左侧竖直布局 */
@media (max-width: 768px) {
  .main-layout {
    gap: 12px;
    padding: 12px;
  }
  
  .category-sidebar {
    width: 80px;
    padding: 12px 8px;
  }
  
  .category-title {
    font-size: 14px;
    margin-bottom: 8px;
  }
  
  .category-item {
    padding: 12px 6px;
    min-height: 70px;
  }
  
  .category-icon {
    font-size: 24px;
  }
  
  .category-name {
    font-size: 11px;
  }
  
  .product-item {
    grid-template-columns: 70px 1fr;
  }
  
  .product-checkbox-placeholder {
    display: none;
  }
}

/* 超小屏幕（手机竖屏）：顶部横向 */
@media (max-width: 480px) {
  .main-layout {
    flex-direction: column;
  }
  
  .category-sidebar {
    width: 100%;
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    gap: 8px;
    padding: 12px;
    -webkit-overflow-scrolling: touch;
  }
  
  .category-title {
    display: none;
  }
  
  .category-item {
    min-width: 60px;
    margin-bottom: 0;
    min-height: auto;
  }
}

/* APP 下载浮动按钮容器 */
.app-download-container {
  position: fixed;
  bottom: 160px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 999;
}

.app-download-float-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 10px 18px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 500;
  font-size: 13px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.app-download-float-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
}

.android-btn {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  box-shadow: 0 4px 12px rgba(17, 153, 142, 0.4);
}

.ios-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

@media (max-width: 768px) {
  .app-download-container {
    bottom: 140px;
    right: 12px;
    gap: 8px;
  }
  
  .app-download-float-btn {
    padding: 8px 14px;
    font-size: 12px;
  }
}

/* WhatsApp 浮动按钮 */
.whatsapp-float-btn {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: #25D366;
  color: white;
  padding: 14px 24px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
  z-index: 999;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.whatsapp-float-btn:hover {
  background: #1da851;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(37, 211, 102, 0.5);
}

@media (max-width: 768px) {
  .whatsapp-float-btn {
    bottom: 20px;
    right: 20px;
    padding: 12px 20px;
    font-size: 14px;
  }
}
</style>
